import {
  DesktopAccessDisabled,
  DesktopWindows,
  DesktopWindowsOutlined,
  LiveTv,
  TvOff,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { DeviceStatus } from '~/components/devices';
import type { DeviceIndicators__Device as Device } from './device-indicators.generated';

/* GraphQL */ `#graphql
  fragment DeviceIndicators__Device on Device {
    activationChangedAt
    active
    activeShow {
      show {
        id
        channelGuide {
          id
          channelCount
        } 
      }
      }
    displayOn
    id
    timeSinceUpdate {
      ...Interval
    }
  }
`;

interface DeviceDisplayIndicatorProps {
  device: Device;
}

export const DeviceDisplayIndicator = ({ device }: DeviceDisplayIndicatorProps) => {
  const { t } = useTranslation();
  const title =
    device.displayOn === true
      ? t('common.device_indicators.display_on')
      : device.displayOn === false
      ? t('common.device_indicators.display_off')
      : t('common.device_indicators.display_unknown');

  // The icon is the only sign of the status, so it names itself (WCAG 1.1.1); the tooltip
  // repeats that name for the pointer, not as a second description.
  return (
    <Box display="flex" alignItems="center">
      <Tooltip describeChild={false} title={title}>
        {device.displayOn === true ? (
          <DesktopWindowsOutlined titleAccess={title} sx={{ color: 'success.main' }} />
        ) : device.displayOn === false ? (
          <DesktopWindows titleAccess={title} sx={{ color: 'primary.dark' }} />
        ) : (
          <DesktopAccessDisabled titleAccess={title} sx={{ color: 'text.secondary' }} />
        )}
      </Tooltip>
    </Box>
  );
};

interface DeviceChannelIndicatorProps {
  device: Device;
}

export const DeviceChannelIndicator = ({ device }: DeviceChannelIndicatorProps) => {
  const { t } = useTranslation();
  const channelCount = device.activeShow?.show.channelGuide?.channelCount ?? 0;
  const title =
    channelCount > 0
      ? t('common.device_indicators.iptv_channels_available', {
          count: channelCount,
          channels: t('common.pluralize.channel_plural', { count: channelCount }),
        })
      : t('common.device_indicators.no_iptv_channels');

  return (
    <Box display="flex" alignItems="center">
      <Tooltip describeChild={false} title={title}>
        {/* A different icon, not just a different color, tells the two states apart (WCAG 1.4.1). */}
        {channelCount > 0 ? (
          <LiveTv titleAccess={title} sx={{ color: 'success.main' }} />
        ) : (
          <TvOff titleAccess={title} sx={{ color: 'text.secondary' }} />
        )}
      </Tooltip>
    </Box>
  );
};

export interface DeviceIndicatorsProps {
  device: Device;
}

export const DeviceIndicators = ({ device }: DeviceIndicatorsProps) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
      <DeviceStatus device={device} />
      <DeviceDisplayIndicator device={device} />
      <DeviceChannelIndicator device={device} />
    </Box>
  );
};
